import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { api, placeOrder, stripeCheckoutOrder, validateDiscount } from "../lib/api";
import { langField } from "../lib/i18n";

export default function Checkout() {
  const { t, lang, cart, subtotal, clearCart, currency, formatPrice } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null); // {code, discount_amount, total}
  const [discountError, setDiscountError] = useState("");
  const [validating, setValidating] = useState(false);
  const [form, setForm] = useState({
    customer_email: "",
    customer_name: "",
    customer_phone: "",
    shipping_method: "courier",
    pickup_artist_slug: "",
    pickup_event_id: "",
    shipping_address: "",
    apartment: "",
    city: "",
    postal_code: "",
    country: "Portugal",
    notes: "",
  });
  const [roster, setRoster] = useState({ wrestlers: [], musicians: [] });
  const [events, setEvents] = useState([]);
  const [shippingFee, setShippingFee] = useState(0);

  // Load roster + events for the pickup dropdowns.
  useEffect(() => {
    api.get("/roster").then(({ data }) => {
      setRoster({ wrestlers: data?.wrestlers || [], musicians: data?.musicians || [] });
    }).catch(() => {});
    api.get("/events").then(({ data }) => {
      const now = new Date();
      const upcoming = (data || []).filter((e) => {
        const d = new Date(e.date);
        return isNaN(d.getTime()) || d >= new Date(now.getTime() - 24 * 60 * 60 * 1000);
      });
      setEvents(upcoming);
    }).catch(() => {});
  }, []);

  // Recompute shipping fee whenever country / method changes.
  useEffect(() => {
    const params = new URLSearchParams({
      country: form.country || "",
      shipping_method: form.shipping_method || "courier",
    });
    api.get(`/shipping/quote?${params.toString()}`)
      .then(({ data }) => setShippingFee(Number(data?.shipping_fee || 0)))
      .catch(() => setShippingFee(0));
  }, [form.country, form.shipping_method]);

  // Prefill from the logged-in customer's saved address (if any).
  useEffect(() => {
    if (!user || user.role !== "customer") return;
    api.get("/portal/customer/me")
      .then(({ data }) => {
        const sa = data?.saved_address || {};
        setForm((prev) => ({
          ...prev,
          customer_email: prev.customer_email || data?.email || "",
          customer_name: prev.customer_name || data?.name || "",
          customer_phone: prev.customer_phone || sa.customer_phone || "",
          shipping_address: prev.shipping_address || sa.shipping_address || "",
          apartment: prev.apartment || sa.apartment || "",
          city: prev.city || sa.city || "",
          postal_code: prev.postal_code || sa.postal_code || "",
          country: prev.country || sa.country || "Portugal",
        }));
      })
      .catch(() => {});
  }, [user]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildItems = () =>
    cart.map((i) => ({
      product_id: i.product_id,
      name: i.name,
      price: i.price,
      quantity: i.quantity,
      size: i.size || null,
      color: i.color || null,
      image: i.image || null,
    }));

  const applyDiscount = async () => {
    setDiscountError("");
    setValidating(true);
    try {
      const r = await validateDiscount(discountCode, buildItems());
      setDiscountApplied(r);
    } catch (err) {
      setDiscountApplied(null);
      setDiscountError(err?.response?.data?.detail || "Invalid code");
    } finally {
      setValidating(false);
    }
  };

  const removeDiscount = () => {
    setDiscountApplied(null);
    setDiscountCode("");
    setDiscountError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await stripeCheckoutOrder({
        ...form,
        items: buildItems(),
        discount_code: discountApplied?.code || null,
        language: lang,
      });
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      throw new Error("No checkout URL received");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (orderResult) {
    return (
      <div
        className="max-w-[800px] mx-auto px-5 md:px-10 py-20 text-center"
        data-testid="checkout-success"
      >
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">⚡ ORDER CONFIRMED</p>
        <h1 className="font-display text-6xl md:text-8xl mt-4 leading-none">
          {t.checkout.success}
        </h1>
        <p className="text-kult-muted mt-4 text-lg">{t.checkout.success_sub}</p>
        <p className="font-mono text-sm bg-kult-yellow text-black inline-block px-3 py-1 mt-6">
          {orderResult.order_number}
        </p>
        <p className="font-mono text-xs text-kult-muted mt-3">
          {formatPrice(orderResult.total)} — {t.checkout.mock_disclaimer}
        </p>
        <div className="mt-10 flex justify-center gap-4">
          <Link
            to="/"
            className="bg-kult-pink text-black px-8 py-4 font-display text-xl"
            data-testid="checkout-back-home"
          >
            {t.checkout.back_home}
          </Link>
          <Link to="/shop" className="border-2 border-white px-8 py-4 font-display text-xl">
            {t.cart.continue}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16"
      data-testid="page-checkout"
    >
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ CHECKOUT</p>
      <h1 className="font-display text-5xl md:text-7xl mt-2 leading-none">{t.checkout.title}</h1>
      <p className="font-mono text-xs text-kult-muted mt-2">Secured by Stripe · test mode</p>

      {cart.length === 0 ? (
        <div className="py-20 text-center">
          <p className="font-mono text-sm tracking-widest text-kult-muted">{t.cart.empty}</p>
          <Link
            to="/shop"
            className="inline-block mt-6 bg-kult-pink text-black px-6 py-3 font-display text-xl"
          >
            {t.cart.continue}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-10">
          <form
            onSubmit={submit}
            className="lg:col-span-7 space-y-8"
            data-testid="checkout-form"
          >
            <fieldset className="border border-kult-border p-6">
              <legend className="px-2 font-mono text-xs tracking-[0.3em] text-kult-yellow">
                {t.checkout.contact}
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={t.checkout.email}
                  type="email"
                  required
                  value={form.customer_email}
                  onChange={onChange("customer_email")}
                  testId="checkout-email"
                />
                <Field
                  label={t.checkout.name}
                  required
                  value={form.customer_name}
                  onChange={onChange("customer_name")}
                  testId="checkout-name"
                />
                <Field
                  label={lang === "pt" ? "TELEFONE" : "PHONE"}
                  type="tel"
                  required
                  value={form.customer_phone}
                  onChange={onChange("customer_phone")}
                  testId="checkout-phone"
                  className="md:col-span-2"
                />
              </div>
            </fieldset>

            <fieldset className="border border-kult-border p-6">
              <legend className="px-2 font-mono text-xs tracking-[0.3em] text-kult-yellow">
                {t.checkout.shipping}
              </legend>

              {/* COUNTRY FIRST — determines courier shipping fee (€5 PT / €20 international) */}
              <div className="mb-5" data-testid="checkout-country-block">
                <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">
                  {lang === "pt" ? "PAÍS DE ENTREGA *" : "DELIVERY COUNTRY *"}
                </p>
                <select
                  required
                  value={form.country}
                  onChange={onChange("country")}
                  className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                  data-testid="checkout-country"
                >
                  <option value="Portugal">Portugal — {formatPrice(5, { decimals: 0 })}</option>
                  <option value="">{lang === "pt" ? "— Outro país (internacional) —" : "— Other country (international) —"}</option>
                  {["Spain","France","Italy","Germany","United Kingdom","Ireland","Netherlands","Belgium","Switzerland","Austria","Sweden","Denmark","Norway","Finland","Poland","Czech Republic","Greece","Brazil","United States","Canada","Mexico","Australia","Japan","Other"].map((c) => (
                    <option key={c} value={c}>{c} — {formatPrice(20, { decimals: 0 })}</option>
                  ))}
                </select>
                {form.shipping_method === "courier" && (
                  <p className="font-mono text-[11px] text-kult-yellow mt-2" data-testid="checkout-shipping-fee">
                    {lang === "pt" ? "Taxa de envio" : "Shipping fee"}: {formatPrice(shippingFee)}
                  </p>
                )}
              </div>

              {/* Shipping method selector */}
              <div className="space-y-2 mb-5" data-testid="checkout-method-selector">
                {[
                  {
                    v: "courier",
                    label: lang === "pt" ? "ENVIO POR TRANSPORTADORA" : "COURIER DELIVERY",
                    sub: lang === "pt"
                      ? "As peças são feitas por encomenda. Envio em 7–10 dias úteis. Entrega doméstica em 7–10 dias úteis; internacional até 21 dias."
                      : "Made-to-order. Ships in 7–10 business days. Domestic delivery in 7–10 business days; international up to 21 days.",
                    priceLabel: null,
                  },
                  {
                    v: "pickup_roster",
                    label: lang === "pt" ? "LEVANTAR COM UM MEMBRO DO ROSTER" : "PICK-UP WITH A ROSTER MEMBER",
                    sub: lang === "pt"
                      ? "Contacta-o nas redes sociais para combinar o levantamento da tua encomenda."
                      : "Reach out to them on social media to arrange picking up your order.",
                    priceLabel: lang === "pt" ? "GRÁTIS" : "FREE",
                  },
                  {
                    v: "pickup_event",
                    label: lang === "pt" ? "LEVANTAR NUM EVENTO" : "PICK-UP AT AN EVENT",
                    sub: lang === "pt" ? "Encontra-nos no evento e levantas a tua encomenda." : "Meet us at the event and pick up your order.",
                    priceLabel: lang === "pt" ? "GRÁTIS" : "FREE",
                  },
                ].map((opt) => {
                  const checked = form.shipping_method === opt.v;
                  return (
                    <label
                      key={opt.v}
                      className={`block border-2 p-3 cursor-pointer transition-colors ${checked ? "border-kult-pink bg-kult-black2" : "border-kult-border hover:border-white"}`}
                      data-testid={`checkout-method-${opt.v}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="shipping_method"
                          value={opt.v}
                          checked={checked}
                          onChange={() => setForm({ ...form, shipping_method: opt.v })}
                          className="mt-1 accent-pink-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-display text-lg leading-tight">{opt.label}</span>
                            {opt.priceLabel && (
                              <span className="font-mono text-[10px] tracking-[0.25em] bg-kult-yellow text-black px-2 py-0.5">
                                {opt.priceLabel}
                              </span>
                            )}
                          </div>
                          <p className="font-mono text-[11px] text-kult-muted mt-1">{opt.sub}</p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              {/* Pickup-with-roster dropdown */}
              {form.shipping_method === "pickup_roster" && (
                <div className="mb-5" data-testid="checkout-pickup-roster-block">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">
                    {lang === "pt" ? "QUEM ENTREGA?" : "WHO HANDS IT OVER?"}
                  </p>
                  <select
                    required
                    value={form.pickup_artist_slug}
                    onChange={onChange("pickup_artist_slug")}
                    className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                    data-testid="checkout-pickup-roster-select"
                  >
                    <option value="">{lang === "pt" ? "— Escolhe um membro —" : "— Pick a member —"}</option>
                    {roster.wrestlers.length > 0 && (
                      <optgroup label={lang === "pt" ? "Wrestlers" : "Wrestlers"}>
                        {roster.wrestlers.map((a) => (
                          <option key={a.slug} value={a.slug}>{a.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {roster.musicians.length > 0 && (
                      <optgroup label={lang === "pt" ? "Músicos" : "Musicians"}>
                        {roster.musicians.map((a) => (
                          <option key={a.slug} value={a.slug}>{a.name}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}

              {/* Pickup-at-event dropdown */}
              {form.shipping_method === "pickup_event" && (
                <div className="mb-5" data-testid="checkout-pickup-event-block">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">
                    {lang === "pt" ? "EM QUE EVENTO?" : "WHICH EVENT?"}
                  </p>
                  <select
                    required
                    value={form.pickup_event_id}
                    onChange={onChange("pickup_event_id")}
                    className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
                    data-testid="checkout-pickup-event-select"
                  >
                    <option value="">{lang === "pt" ? "— Escolhe um evento —" : "— Pick an event —"}</option>
                    {events.map((e) => {
                      const d = new Date(e.date);
                      const dateLabel = isNaN(d.getTime())
                        ? ""
                        : d.toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "2-digit", month: "short", year: "numeric" });
                      return (
                        <option key={e.id} value={e.id}>
                          {dateLabel ? `${dateLabel.toUpperCase()} · ` : ""}{(lang === "pt" ? (e.title_pt || e.title) : e.title)} — {e.city}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {form.shipping_method === "courier" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label={t.checkout.address}
                    required
                    value={form.shipping_address}
                    onChange={onChange("shipping_address")}
                    className="md:col-span-2"
                    testId="checkout-address"
                  />
                  <Field
                    label={t.checkout.apartment}
                    value={form.apartment}
                    onChange={onChange("apartment")}
                    className="md:col-span-2"
                    testId="checkout-apartment"
                    placeholder={lang === "pt" ? "Ex. 3º Esq, Bloco B, Apt 12" : "Ex. Apt 4B, Suite 12, Floor 3"}
                  />
                  <Field
                    label={t.checkout.city}
                    required
                    value={form.city}
                    onChange={onChange("city")}
                    testId="checkout-city"
                  />
                  <Field
                    label={t.checkout.postal}
                    required
                    value={form.postal_code}
                    onChange={onChange("postal_code")}
                    testId="checkout-postal"
                  />
                  <Field
                    label={t.checkout.notes}
                    value={form.notes}
                    onChange={onChange("notes")}
                    className="md:col-span-2"
                    testId="checkout-notes"
                  />
                </div>
              ) : (
                <Field
                  label={t.checkout.notes}
                  value={form.notes}
                  onChange={onChange("notes")}
                  testId="checkout-notes"
                />
              )}
            </fieldset>

            {error && (
              <p className="font-mono text-sm text-kult-pink">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-kult-pink text-black font-display text-2xl py-5 hover:bg-kult-yellow transition-colors disabled:opacity-50"
              data-testid="checkout-submit"
            >
              {submitting ? "..." : t.checkout.place_order}
            </button>
          </form>

          <aside className="lg:col-span-5">
            <div className="sticky top-24 border border-kult-border p-6 bg-kult-black2">
              <h3 className="font-display text-2xl mb-4">{t.cart.title}</h3>
              <div className="divide-y divide-kult-border">
                {cart.map((i, idx) => (
                  <div key={idx} className="flex gap-3 py-3" data-testid={`checkout-item-${idx}`}>
                    {i.image && <img src={i.image} alt="" className="w-16 h-20 object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-sm leading-tight">
                        {langField(i, "name", lang)}
                      </p>
                      <p className="font-mono text-[10px] text-kult-muted mt-1">
                        {i.size && `${i.size}`}
                        {i.color ? ` / ${i.color}` : ""} · ×{i.quantity}
                      </p>
                    </div>
                    <span className="font-mono text-sm">
                      {formatPrice(i.price * i.quantity, { decimals: 0 })}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-kult-border mt-4 pt-4 space-y-3">
                <div className="space-y-2" data-testid="checkout-discount-section">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">DISCOUNT CODE</p>
                  {discountApplied ? (
                    <div className="flex items-center justify-between border border-kult-yellow px-3 py-2" data-testid="checkout-discount-applied">
                      <div>
                        <span className="font-mono text-xs text-kult-yellow">{discountApplied.code}</span>
                        <span className="font-mono text-[10px] text-kult-muted ml-2">
                          −{formatPrice(discountApplied.discount_amount)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={removeDiscount}
                        className="font-mono text-[10px] tracking-[0.25em] text-kult-pink"
                        data-testid="checkout-discount-remove"
                      >REMOVE</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="KULT10"
                        className="flex-1 bg-transparent border border-kult-border px-3 py-2 font-mono text-xs uppercase focus:border-kult-pink outline-none"
                        data-testid="checkout-discount-input"
                      />
                      <button
                        type="button"
                        onClick={applyDiscount}
                        disabled={!discountCode || validating}
                        className="bg-kult-yellow text-black px-4 py-2 font-mono text-[10px] tracking-[0.25em] disabled:opacity-50"
                        data-testid="checkout-discount-apply"
                      >APPLY</button>
                    </div>
                  )}
                  {discountError && (
                    <p className="font-mono text-[10px] text-kult-pink" data-testid="checkout-discount-error">
                      {discountError}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.25em]">{t.cart.subtotal}</span>
                  <span className="font-mono text-sm" data-testid="checkout-subtotal">
                    {formatPrice(subtotal)}
                  </span>
                </div>
                {discountApplied && (
                  <div className="flex items-center justify-between text-kult-yellow">
                    <span className="font-mono text-xs tracking-[0.25em]">DISCOUNT</span>
                    <span className="font-mono text-sm">−{formatPrice(discountApplied.discount_amount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-[0.25em]">
                    {lang === "pt" ? "ENVIO" : "SHIPPING"}
                  </span>
                  <span className="font-mono text-sm" data-testid="checkout-summary-shipping">
                    {shippingFee === 0
                      ? (lang === "pt" ? "GRÁTIS" : "FREE")
                      : formatPrice(shippingFee)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-kult-border pt-3">
                  <span className="font-mono text-xs tracking-[0.25em]">TOTAL</span>
                  <span
                    className="font-display text-3xl bg-kult-yellow text-black px-2"
                    data-testid="checkout-total"
                  >
                    {formatPrice((discountApplied ? discountApplied.total : subtotal) + shippingFee)}
                  </span>
                </div>
                {currency !== "EUR" && (
                  <p className="font-mono text-[10px] text-kult-muted leading-relaxed pt-1" data-testid="checkout-currency-note">
                    {lang === "pt"
                      ? `Prices shown in ${currency} are estimates. Your card will be charged in EUR.`
                      : `Prices shown in ${currency} are estimates. Your card will be charged in EUR.`}
                  </p>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Field({ label, className = "", testId, ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">
        {label}
      </span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
      />
    </label>
  );
}
